import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'

export interface CarrierOption {
  id: number
  name: string
  code?: string
}

export interface CarrierAppointmentInput {
  id?: number
  carrier_id: number | ''
  producer_number: string
  _destroy?: boolean
  _localKey?: string
}

interface Props {
  appointments: CarrierAppointmentInput[]
  carriers: CarrierOption[]
  onChange: (next: CarrierAppointmentInput[]) => void
  readOnly?: boolean
}

let _localCounter = 0

export default function CarrierAppointmentsEditor({ appointments, carriers, onChange, readOnly }: Props) {
  const [error, setError] = useState<string | null>(null)

  const visible = appointments.filter(a => !a._destroy)

  const update = (idx: number, patch: Partial<CarrierAppointmentInput>) => {
    const visibleIndices = appointments.map((a, i) => (!a._destroy ? i : -1)).filter(i => i >= 0)
    const realIdx = visibleIndices[idx]
    const next = appointments.slice()
    next[realIdx] = { ...next[realIdx], ...patch }
    onChange(next)
  }

  const remove = (idx: number) => {
    const visibleIndices = appointments.map((a, i) => (!a._destroy ? i : -1)).filter(i => i >= 0)
    const realIdx = visibleIndices[idx]
    const target = appointments[realIdx]
    let next: CarrierAppointmentInput[]
    if (target.id) {
      next = appointments.slice()
      next[realIdx] = { ...target, _destroy: true }
    } else {
      next = appointments.filter((_, i) => i !== realIdx)
    }
    onChange(next)
  }

  const add = () => {
    if (carriers.length === 0) {
      setError('No carriers are set up yet. Ask an admin to add a carrier first.')
      return
    }
    setError(null)
    _localCounter += 1
    onChange([
      ...appointments,
      {
        carrier_id: carriers[0].id,
        producer_number: '',
        _localKey: `new-${_localCounter}`
      }
    ])
  }

  return (
    <div className="space-y-3">
      {visible.length === 0 ? (
        <p className="text-sm text-chestnut-charcoal/60">No carrier appointments on file.</p>
      ) : (
        <div className="space-y-2">
          {visible.map((a, idx) => (
            <div
              key={a.id ?? a._localKey ?? `idx-${idx}`}
              className="flex flex-col md:flex-row md:items-end gap-3 p-3 bg-chestnut-cream/40 rounded-lg"
            >
              <div className="flex-1">
                <label className="block text-xs font-semibold text-chestnut-charcoal/70 mb-1">Carrier</label>
                <select
                  value={a.carrier_id}
                  onChange={(e) => update(idx, { carrier_id: e.target.value ? Number(e.target.value) : '' })}
                  className="input-field w-full"
                  disabled={readOnly}
                >
                  <option value="">Select a carrier…</option>
                  {carriers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-semibold text-chestnut-charcoal/70 mb-1">Writing / Producer Number</label>
                <input
                  type="text"
                  value={a.producer_number}
                  onChange={(e) => update(idx, { producer_number: e.target.value })}
                  className="input-field w-full"
                  placeholder="e.g. AG12345"
                  disabled={readOnly}
                  maxLength={100}
                />
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg self-start md:self-auto"
                  aria-label="Remove appointment"
                  title="Remove appointment"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {error && (
        <p className="text-sm text-status-error">{error}</p>
      )}

      {!readOnly && (
        <button
          type="button"
          onClick={add}
          className="btn-secondary inline-flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Carrier
        </button>
      )}
    </div>
  )
}
